<template>
  <div class="draft-editor">
    <div class="draft-editor__toolbar">
      <v-btn-toggle
        v-if="isNarrow"
        v-model="narrowPane"
        dense
        mandatory
        color="primary"
        class="draft-editor__pane-toggle">
        <v-btn small value="edit">
          <v-icon left small>mdi-pencil-outline</v-icon>
          Edit
        </v-btn>
        <v-btn small value="preview">
          <v-icon left small>mdi-eye-outline</v-icon>
          Preview
        </v-btn>
      </v-btn-toggle>

      <div v-if="regenerating" class="draft-editor__regen-status">
        <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
        <span>{{ regenerateHint }}</span>
      </div>

      <v-spacer />

      <span v-if="isDirty" class="draft-editor__dirty">Unsaved changes</span>

      <v-btn
        v-if="canEdit && !isInternal"
        small
        outlined
        class="ml-2"
        :disabled="regenerating || saving"
        @click="openRegenerateDialog">
        <v-icon left small>mdi-auto-fix</v-icon>
        Regenerate with AI
      </v-btn>

      <v-btn
        v-if="canEdit && isInternal"
        small
        outlined
        class="ml-2"
        :disabled="rebuilding || saving"
        :loading="rebuilding"
        @click="rebuildFromCustomer">
        <v-icon left small>mdi-sync</v-icon>
        Rebuild from customer notes
      </v-btn>

      <v-btn
        v-if="canEdit"
        small
        color="primary"
        class="ml-2"
        :disabled="!isDirty || saving || regenerating"
        :loading="saving"
        @click="save">
        <v-icon left small>mdi-content-save-outline</v-icon>
        Save
      </v-btn>
    </div>

    <v-alert
      v-if="serverChangedHint"
      dense
      text
      type="info"
      class="draft-editor__alert"
      icon="mdi-cloud-alert-outline">
      <div class="d-flex align-center flex-wrap">
        <span class="mr-auto">
          This draft changed on the server. Your unsaved edits are still here.
        </span>
        <v-btn text x-small color="info" @click="loadLatestFromRelease">Load latest</v-btn>
        <v-btn text x-small @click="serverChangedHint = false">Dismiss</v-btn>
      </div>
    </v-alert>

    <v-alert v-if="inlineError" dense text type="error" class="draft-editor__alert">
      {{ inlineError }}
    </v-alert>

    <v-row dense class="draft-editor__panes">
      <v-col v-if="showSource" cols="12" :md="isNarrow ? 12 : 6">
        <div class="ds-field-label">Markdown source</div>
        <v-textarea
          v-model="localMarkdown"
          outlined
          dense
          hide-details
          no-resize
          class="draft-editor__source"
          :rows="textareaRows"
          :readonly="!canEdit"
          placeholder="# What's new&#10;&#10;- Describe the change…" />
        <div class="draft-editor__hint">
          <span>Markdown supported — headings, lists, links, bold.</span>
          <span>{{ wordCount }} words · {{ characterCount }} characters</span>
        </div>
      </v-col>

      <v-col v-if="showPreview" cols="12" :md="isNarrow ? 12 : 6">
        <div class="ds-field-label">Preview</div>
        <div class="draft-editor__preview">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="release-body" v-html="previewHtml"></div>
        </div>
        <div class="draft-editor__hint">
          <span>Approximate — the published page uses the server's sanitized HTML.</span>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="regenerateDialog" persistent max-width="560">
      <v-card class="ds-dialog" color="bg1">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary" size="20">mdi-auto-fix</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <div class="ds-dialog__title">Regenerate with AI</div>
            <div class="ds-dialog__subtitle">
              A new draft version is written from the release's tickets. The current version stays
              in the history.
            </div>
          </div>
        </div>

        <div class="ds-dialog__body">
          <div class="ds-field-label">Instructions (optional)</div>
          <v-textarea
            v-model="regenerateInstructions"
            outlined
            dense
            auto-grow
            rows="3"
            hide-details
            placeholder="e.g. Keep it under 200 words and lead with the security fixes." />
          <div class="ds-dialog__hint mt-2">
            Any unsaved edits in the editor will be replaced once the new draft arrives.
          </div>
        </div>

        <div class="ds-dialog__footer">
          <v-spacer />
          <v-btn text small :disabled="regenerating" @click="regenerateDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" small :loading="regenerating" @click="regenerate">
            Regenerate
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <draft-conflict-dialog
      :value="conflictDialog"
      :local-markdown="localMarkdown"
      :server-draft="conflictDraft"
      :saving="saving"
      @input="conflictDialog = $event"
      @keep-mine="keepMine"
      @load-theirs="loadTheirs" />
  </div>
</template>

<script>
  import { marked } from 'marked';
  import permissions from '@Share/config/permissions';
  import { $has } from '@Utils';
  import { log, warn, error } from '@/utils/debugLogger';
  import { releaseErrorCode, releaseErrorMessage, ReleaseDraftKind } from '@ReleaseNotes/config/enums';
  import DraftConflictDialog from './DraftConflictDialog.vue';

  // Regenerate is asynchronous server-side and does NOT flip the release Status, so the
  // only observable signal is a new draft version appearing in GET {id}/drafts.
  const REGENERATE_POLL_INTERVAL_MS = 3000;
  const REGENERATE_POLL_TIMEOUT_MS = 120000;

  export default {
    name: 'DraftEditor',
    components: { DraftConflictDialog },
    props: {
      release: {
        type: Object,
        default: null,
      },
      releaseId: {
        type: [String, Number],
        default: null,
      },
      /** Which note stream this editor is bound to — customer-facing or internal. */
      kind: {
        type: Number,
        default: ReleaseDraftKind.Customer,
      },
    },
    data() {
      return {
        permissions,
        localMarkdown: '',
        originalMarkdown: '',
        baseDraftId: null,
        saving: false,
        inlineError: '',
        serverChangedHint: false,
        narrowPane: 'edit',
        regenerateDialog: false,
        regenerateInstructions: '',
        regenerating: false,
        regenerateSlow: false,
        regenerateBaseVersion: null,
        pollTimer: null,
        pollDeadline: 0,
        pollInFlight: false,
        conflictDialog: false,
        conflictDraft: null,
        rebuilding: false,
        unloadGuardAttached: false,
      };
    },
    computed: {
      canEdit() {
        return $has(permissions.ReleaseNotes.Edit);
      },
      isInternal() {
        return this.kind === ReleaseDraftKind.Internal;
      },
      isNarrow() {
        return this.$vuetify.breakpoint.smAndDown;
      },
      showSource() {
        return !this.isNarrow || this.narrowPane === 'edit';
      },
      showPreview() {
        return !this.isNarrow || this.narrowPane === 'preview';
      },
      isDirty() {
        return this.localMarkdown !== this.originalMarkdown;
      },
      textareaRows() {
        return this.isNarrow ? 16 : 24;
      },
      characterCount() {
        return this.localMarkdown.length;
      },
      wordCount() {
        const trimmed = this.localMarkdown.trim();
        return trimmed ? trimmed.split(/\s+/).length : 0;
      },
      previewHtml() {
        if (!this.localMarkdown.trim()) {
          return '<p><em>Nothing to preview yet.</em></p>';
        }
        try {
          // marked v15 parses synchronously unless async mode is opted into; keep it sync
          // so the preview stays a plain computed (matches the Chatbot usage).
          return marked.parse(this.localMarkdown);
        } catch (err) {
          warn('DraftEditor: markdown preview failed', err);
          return '<p><em>Preview unavailable — check the Markdown syntax.</em></p>';
        }
      },
      regenerateHint() {
        return this.regenerateSlow
          ? 'Still generating — this is taking longer than expected.'
          : 'Generating a new draft…';
      },
    },
    watch: {
      release: {
        immediate: true,
        handler(value) {
          this.applyRelease(value);
        },
      },
      isDirty(value) {
        if (value) this.addUnloadGuard();
        else this.removeUnloadGuard();
      },
    },
    beforeDestroy() {
      this.stopPolling();
      this.removeUnloadGuard();
    },
    methods: {
      $has,

      // ------------------------------------------------------------- DTO probing
      /**
       * The release detail DTO's draft shape is not contract-verified (plan §2 only says the
       * detail returns "current draft version+markdown"), so the markdown is probed across
       * the spellings the API could plausibly use, first match wins.
       */
      releaseMarkdown(release) {
        if (!release) return '';
        if (this.isInternal) {
          const internal = [release.currentInternalDraftMarkdown, release.CurrentInternalDraftMarkdown].find(
            (value) => typeof value === 'string',
          );
          return internal || '';
        }
        const current = release.currentDraft || release.CurrentDraft || null;
        const candidates = [
          current && current.markdown,
          current && current.Markdown,
          release.currentDraftMarkdown,
          release.CurrentDraftMarkdown,
          release.markdown,
          release.Markdown,
        ];
        const found = candidates.find((value) => typeof value === 'string');
        return found || '';
      },
      releaseDraftId(release) {
        if (!release) return null;
        if (this.isInternal) {
          const internal = [release.currentInternalDraftId, release.CurrentInternalDraftId].find(
            (value) => value !== null && value !== undefined,
          );
          return internal === undefined ? null : internal;
        }
        const current = release.currentDraft || release.CurrentDraft || null;
        const candidates = [
          current && current.id,
          current && current.Id,
          release.currentDraftId,
          release.CurrentDraftId,
        ];
        const found = candidates.find((value) => value !== null && value !== undefined);
        return found === undefined ? null : found;
      },
      releaseDraftVersion(release) {
        if (!release) return null;
        if (this.isInternal) {
          const internal = [release.currentInternalDraftVersion, release.CurrentInternalDraftVersion].find(
            (value) => typeof value === 'number',
          );
          return internal === undefined ? null : internal;
        }
        const current = release.currentDraft || release.CurrentDraft || null;
        const candidates = [
          current && current.version,
          current && current.Version,
          release.currentDraftVersion,
          release.CurrentDraftVersion,
        ];
        const found = candidates.find((value) => typeof value === 'number');
        return found === undefined ? null : found;
      },
      draftIdOf(draft) {
        if (!draft) return null;
        const candidates = [draft.id, draft.Id, draft.draftId, draft.DraftId];
        const found = candidates.find((value) => value !== null && value !== undefined);
        return found === undefined ? null : found;
      },
      draftMarkdownOf(draft) {
        if (!draft) return '';
        const candidates = [draft.markdown, draft.Markdown, draft.content, draft.body];
        const found = candidates.find((value) => typeof value === 'string');
        return found || '';
      },
      draftVersionOf(draft) {
        if (!draft) return null;
        const candidates = [draft.version, draft.Version, draft.versionNumber];
        const found = candidates.find((value) => typeof value === 'number');
        return found === undefined ? null : found;
      },

      // --------------------------------------------------------------- (re)seed
      /**
       * Seeds the editor from the release prop. A parent reload (after regenerate or revert)
       * re-runs this; unsaved local edits are never silently discarded — instead the user is
       * told the server copy moved and offered an explicit "load latest".
       */
      applyRelease(release) {
        const incoming = this.releaseMarkdown(release);
        const incomingId = this.releaseDraftId(release);

        if (this.isDirty && incoming !== this.originalMarkdown) {
          this.serverChangedHint = true;
          return;
        }

        this.localMarkdown = incoming;
        this.originalMarkdown = incoming;
        this.baseDraftId = incomingId;
        this.serverChangedHint = false;
        this.inlineError = '';
      },
      loadLatestFromRelease() {
        const incoming = this.releaseMarkdown(this.release);
        this.localMarkdown = incoming;
        this.originalMarkdown = incoming;
        this.baseDraftId = this.releaseDraftId(this.release);
        this.serverChangedHint = false;
        this.inlineError = '';
      },

      // -------------------------------------------------------------- rebuild
      /**
       * Re-derives the internal notes from the current customer notes. Per-bullet ticket references
       * cannot be recovered from prose that has been edited since generation, so this is destructive
       * to hand-written internal wording — hence the confirm. The previous version stays in history.
       */
      async rebuildFromCustomer() {
        const confirmed = await this.$dialog.confirm({
          title: 'Rebuild internal notes?',
          text:
            'This replaces the internal notes with the current customer notes plus the linked ticket list. ' +
            'Per-bullet ticket references from the original AI draft are not restored, and any internal-only ' +
            'wording you added is replaced. The current version stays in history, so you can revert.',
        });
        if (!confirmed) return;

        this.rebuilding = true;
        try {
          await this.$store.dispatch('RELEASE_NOTES/REBUILD_INTERNAL_NOTES', this.releaseId);
          this.$toast?.success('Internal notes rebuilt.');
          this.$emit('reload');
        } catch (err) {
          warn('DraftEditor: internal notes rebuild failed', err);
        } finally {
          this.rebuilding = false;
        }
      },

      // ------------------------------------------------------------------- save
      save() {
        if (!this.canEdit || this.saving) return Promise.resolve();
        if (!this.localMarkdown.trim()) {
          // RELEASE_DRAFT_TOO_LARGE also covers "empty" server-side; block it here so the
          // user gets an inline hint instead of a round-trip and a toast.
          this.inlineError = 'Release notes cannot be empty. Write something before saving.';
          return Promise.resolve();
        }
        this.inlineError = '';
        return this.dispatchSave(this.baseDraftId);
      },
      dispatchSave(baseDraftId) {
        this.saving = true;
        return this.$store
          .dispatch('RELEASE_NOTES/SAVE_DRAFT', {
            id: this.releaseId,
            data: { kind: this.kind, baseDraftId, markdown: this.localMarkdown },
          })
          .then((data) => {
            this.onSaved(data);
          })
          .catch((err) => {
            this.onSaveFailed(err);
          })
          .finally(() => {
            this.saving = false;
          });
      },
      onSaved(data) {
        const savedMarkdown = this.draftMarkdownOf(data);
        // The response is the new draft; trust its markdown when present, otherwise keep
        // exactly what was sent (they are the same text by construction).
        this.originalMarkdown = savedMarkdown || this.localMarkdown;
        if (savedMarkdown) this.localMarkdown = savedMarkdown;
        const newId = this.draftIdOf(data);
        if (newId !== null) this.baseDraftId = newId;
        this.conflictDialog = false;
        this.conflictDraft = null;
        this.serverChangedHint = false;
        this.inlineError = '';
        log('DraftEditor: draft saved', { releaseId: this.releaseId, draftId: this.baseDraftId });
        this.$dialog.notify.success('Draft saved', { position: 'top-right', timeout: 3000 });
        this.$emit('saved');
      },
      onSaveFailed(err) {
        const status = err && err.response ? err.response.status : null;
        if (status === 409) {
          // SAVE_DRAFT deliberately does not toast a 409 (addendum §C) — the conflict is
          // resolved here instead, against the server's current draft.
          const data = err.response.data || {};
          this.conflictDraft = data.currentDraft || data.CurrentDraft || null;
          this.conflictDialog = true;
          warn('DraftEditor: draft save conflict', { releaseId: this.releaseId });
          return;
        }
        if (releaseErrorCode(err) === 'RELEASE_DRAFT_TOO_LARGE') {
          this.inlineError = releaseErrorMessage(err);
        }
        error('DraftEditor: draft save failed', err);
      },
      keepMine() {
        // Retry against the base the server now considers current, so the overwrite is a
        // legitimate successor rather than another stale-base conflict.
        const newBase = this.draftIdOf(this.conflictDraft);
        this.dispatchSave(newBase === null ? this.baseDraftId : newBase);
      },
      loadTheirs() {
        const theirs = this.draftMarkdownOf(this.conflictDraft);
        const theirId = this.draftIdOf(this.conflictDraft);
        this.localMarkdown = theirs;
        this.originalMarkdown = theirs;
        if (theirId !== null) this.baseDraftId = theirId;
        this.conflictDialog = false;
        this.conflictDraft = null;
        this.serverChangedHint = false;
        this.inlineError = '';
        this.$emit('saved');
      },

      // ------------------------------------------------------------- regenerate
      openRegenerateDialog() {
        this.regenerateInstructions = '';
        this.regenerateDialog = true;
      },
      regenerate() {
        this.regenerateBaseVersion = this.currentKnownVersion();
        const instructions = this.regenerateInstructions.trim();
        this.regenerating = true;
        this.regenerateSlow = false;
        return this.$store
          .dispatch('RELEASE_NOTES/REGENERATE_DRAFT', {
            id: this.releaseId,
            data: { instructions: instructions || null },
          })
          .then(() => {
            this.regenerateDialog = false;
            this.startPolling();
          })
          .catch((err) => {
            this.regenerating = false;
            error('DraftEditor: regenerate failed', err);
          });
      },
      currentKnownVersion() {
        const fromRelease = this.releaseDraftVersion(this.release);
        if (fromRelease !== null) return fromRelease;
        const drafts = this.$store.getters['RELEASE_NOTES/GET_DRAFTS'] || [];
        const versions = drafts
          .map((draft) => this.draftVersionOf(draft))
          .filter((value) => value !== null);
        return versions.length ? Math.max(...versions) : 0;
      },
      startPolling() {
        this.stopPolling();
        this.pollDeadline = Date.now() + REGENERATE_POLL_TIMEOUT_MS;
        this.pollTimer = setInterval(this.pollForNewDraft, REGENERATE_POLL_INTERVAL_MS);
      },
      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
        this.pollInFlight = false;
      },
      pollForNewDraft() {
        // One request in flight at a time — a slow API must not stack up polls.
        if (this.pollInFlight) return;
        if (Date.now() > this.pollDeadline) {
          this.stopPolling();
          this.regenerating = false;
          this.regenerateSlow = false;
          this.inlineError =
            'The AI is taking longer than expected. Reload the release in a moment to see the new draft.';
          warn('DraftEditor: regenerate poll timed out', { releaseId: this.releaseId });
          return;
        }
        this.regenerateSlow = Date.now() > this.pollDeadline - REGENERATE_POLL_TIMEOUT_MS / 2;
        this.pollInFlight = true;
        this.$store
          .dispatch('RELEASE_NOTES/FETCH_DRAFTS', this.releaseId)
          .then((data) => {
            const drafts = Array.isArray(data)
              ? data
              : this.$store.getters['RELEASE_NOTES/GET_DRAFTS'] || [];
            const versions = drafts
              .map((draft) => this.draftVersionOf(draft))
              .filter((value) => value !== null);
            const maxVersion = versions.length ? Math.max(...versions) : null;
            if (maxVersion !== null && maxVersion > this.regenerateBaseVersion) {
              this.stopPolling();
              this.regenerating = false;
              this.regenerateSlow = false;
              log('DraftEditor: regenerated draft detected', { version: maxVersion });
              this.$dialog.notify.success('New draft generated', {
                position: 'top-right',
                timeout: 3000,
              });
              this.$emit('regenerated');
            }
          })
          .catch((err) => {
            // FETCH_DRAFTS already toasts; keep polling until the deadline in case the
            // failure was transient.
            warn('DraftEditor: regenerate poll failed', err);
          })
          .finally(() => {
            this.pollInFlight = false;
          });
      },

      // ------------------------------------------------------- unload guarding
      addUnloadGuard() {
        if (this.unloadGuardAttached) return;
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.unloadGuardAttached = true;
      },
      removeUnloadGuard() {
        if (!this.unloadGuardAttached) return;
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        this.unloadGuardAttached = false;
      },
      onBeforeUnload(event) {
        event.preventDefault();
        // Chrome requires returnValue to be set; the string itself is ignored by browsers.
        event.returnValue = '';
        return '';
      },
    },
  };
</script>

<style scoped lang="scss">
  .draft-editor__toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--ds-space-xs, 4px);
    margin-bottom: var(--ds-space-sm, 8px);
  }

  .draft-editor__pane-toggle {
    margin-right: var(--ds-space-sm, 8px);
  }

  .draft-editor__regen-status {
    display: flex;
    align-items: center;
    font-size: 0.8125rem;
    opacity: 0.75;
  }

  .draft-editor__dirty {
    font-size: 0.75rem;
    opacity: 0.6;
    font-style: italic;
  }

  .draft-editor__alert {
    margin-bottom: var(--ds-space-sm, 8px);
  }

  .draft-editor__source ::v-deep textarea {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.8125rem;
    line-height: 1.6;
  }

  .draft-editor__preview {
    border: 1px solid var(--ds-border-subtle, rgba(255, 255, 255, 0.12));
    border-radius: var(--ds-radius-md, 8px);
    padding: var(--ds-space-md, 16px);
    overflow-y: auto;
    min-height: 200px;
    max-height: 60vh;
  }

  .draft-editor__hint {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-space-sm, 8px);
    flex-wrap: wrap;
    font-size: 0.75rem;
    opacity: 0.55;
    margin-top: var(--ds-space-xs, 4px);
  }
</style>
